import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Study } from '../../../../shared/api/hscan/hospital';
import { MobileCheckboxWithText } from '../../common/components/CheckboxWithText';
import {
  MobileSelectAllHeader,
  MobileTable,
  MobileTableFooter,
  MobileTableRow,
  MobileTableWrapper,
  RowContainer,
  RowValue,
} from '../../common/components/MobileTable';
import {
  TableAndElementInformation,
  TableInformation,
} from '../../common/components/SortableTable/PresentationalTable';
import { useModalityLabel } from '../../hooks/modalityHook';

import {
  TableContext,
  TableElementContext,
  TableElementInformationProvider,
} from './MedicalImageTable';
import { StatusMessage } from './StatusMessage';
import { mapStatusForRequest } from './utils';

const HeaderRowLabel = styled.td`
  width: 132px;
  background-color: white;
  padding: 19px 0px;
`;

export const MobileMedicalImageTableElement = (
  props: TableAndElementInformation<Study, 'studyInstanceUID'>,
) => {
  const { element, identifyKey, isSelected, isSelectable, onSelect } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.imageList.table',
  });
  const modalityLabel = useModalityLabel();

  return (
    <MobileTable $round>
      <tbody>
        <RowContainer>
          <HeaderRowLabel align="center">
            <MobileCheckboxWithText
              disabled={!isSelectable}
              value={isSelected}
              onChange={e => onSelect?.(element[identifyKey], e)}
              text={`${t('columns.select')}`}
            />
          </HeaderRowLabel>
          <RowValue align="center">
            <StatusMessage status={mapStatusForRequest(element.status)} />
          </RowValue>
        </RowContainer>
        <MobileTableRow label={`${t('columns.date')}`}>
          {new Date(element.date).toLocaleDateString()}
        </MobileTableRow>
        <MobileTableRow label={`${t('columns.modality')}`}>
          {element.modalities
            .map(modality => modalityLabel(modality))
            .join(', ')}
        </MobileTableRow>
        <MobileTableRow label={`${t('columns.bodypart')}`}>
          {element.studyDescription}
        </MobileTableRow>
      </tbody>
    </MobileTable>
  );
};

const RenderHeader = (props: TableInformation<Study, 'studyInstanceUID'>) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.imageList.table',
  });

  return (
    <MobileSelectAllHeader
      selectAllText={`${t('columns.selectAll')}`}
      selectedCountText={
        props.selected.length
          ? `${t('columns.selectedCount', {
              count: props.selected.length,
            })}`
          : undefined
      }
      {...props}
    />
  );
};

interface MobileMedicalImageTableProps {
  searched: boolean;
}
export const MobileMedicalImageTable = ({
  searched,
}: MobileMedicalImageTableProps) => {
  return (
    <MobileTableWrapper
      tableContext={TableContext}
      elementContext={TableElementContext}
      ElementProvider={TableElementInformationProvider}
      Header={RenderHeader}
      Element={MobileMedicalImageTableElement}
      Footer={MobileTableFooter}
      renderEmpty={props => (
        <MobileEmptyIndicator {...props} searched={searched} />
      )}
    />
  );
};

const MobileEmptyIndicatorWrapper = styled.div`
  text-align: center;
  word-break: keep-all;
`;
const MobileEmptyIndicator = (
  props: TableInformation<Study, 'studyInstanceUID'> & { searched: boolean },
) => {
  const { searched } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.imageList.table',
  });
  return (
    <MobileEmptyIndicatorWrapper>
      {searched ? t('message.empty') : t('message.searchYet')}
    </MobileEmptyIndicatorWrapper>
  );
};
